import { z } from "zod";

import {
  zCompCheckbox,
  zCompDate,
  zCompInput,
  zCompInputNumber,
  zCompSelect,
} from "./metaComponent";
import { zExprBool } from "./metaExpr";
import { zRule } from "./rules";

type MetaNodes = ZMetaNode[];
const zMetaNodes: z.ZodType<MetaNodes> = z.lazy(() => zMetaNode.array());

type ZLayout = {
  layout: ZMetaNode;
};
const zLayout: z.ZodType<ZLayout> = z.lazy(() =>
  z.object({
    layout: zMetaNode,
  }),
);

export const zCompCollapseList = z
  .object({
    type: z.literal("CollapseList"),
    row: zLayout,
  })
  .describe("Для вложенных данных типа recordArray");
export type ZCompCollapseList = z.infer<typeof zCompCollapseList>;

export const zComp = z
  .discriminatedUnion("type", [
    zCompInput,
    zCompInputNumber,
    zCompCheckbox,
    zCompSelect,
    zCompDate,
    zCompCollapseList,
  ])
  .describe("Компонент для редактирования данных на форме");

export const zMetaNodeCommon = {
  colSpan: z
    .union([z.number().int(), z.literal("*")])
    .nullish()
    .describe("Используется для Grid. Значение * чтобы занять всю ширину"),
  disableIf: zExprBool
    .nullish()
    .describe("Правило для запрета текущего узла (и всего его содержимого)"),
  hideIf: zExprBool
    .nullish()
    .describe("Правило для скрытия текущего узла (и всего его содержимого)"),
};

export const zMetaField = z
  .object({
    ...zMetaNodeCommon,
    type: z.literal("field"),
    name: z.string().describe("Служит для связывания со структурой данных"),
    component: zComp,
    title: z.string().nullish().describe("Шаблон"),
    rules: zRule
      .array()
      .nullish()
      .describe("Правила валидации значения данного поля"),
  })
  .describe("Одиночное поле формы");
export type ZMetaField = z.infer<typeof zMetaField>;

export const zMetaGrid = z
  .object({
    ...zMetaNodeCommon,
    type: z.literal("grid"),
    title: z.string().nullish().describe("Необязательный заголовок контейнера"),
    columnsCount: z
      .number()
      .int()
      .min(1)
      .nullish()
      .describe("Количество колонок в сетке"),
    items: zMetaNodes,
  })
  .describe("Контейнер с распределением элементов в виде сетки");
export type ZMetaGrid = z.infer<typeof zMetaGrid>;

const zTab = zLayout
  .and(
    z.object({
      key: z.string(),
      label: z.string(),
    }),
  )
  .describe("Одна секция Tab-контейнера");

export const zMetaTabs = z
  .object({
    ...zMetaNodeCommon,
    type: z.literal("tabs"),
    tabs: zTab.array().min(1),
  })
  .describe("Контейнер с распределением элементов по Tab-секциям");
export type ZMetaTabs = z.infer<typeof zMetaTabs>;

export const zMetaNode = z
  .discriminatedUnion("type", [zMetaField, zMetaGrid, zMetaTabs])
  .describe(
    "Узел описания метаданных.\nФорма загружает корневой узел, который описывает все метаданные формы",
  );

export type ZMetaNode = z.infer<typeof zMetaNode>;
